import styled from 'styled-components';

export const Container = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 0 64px 80px;
	height: 100%;
	overflow-y: auto;
`;

export const Content = styled.div`
	width: 100%;
	max-width: 960px;
	display: flex;
	flex-direction: column;
	gap: 32px;
`;

//----------------------------------------------------------------------
// HERO SECTION
//----------------------------------------------------------------------
export const Hero = styled.div`
	background: linear-gradient(
		135deg,
		${({ theme }) => theme.gray[200]} 0%,
		${({ theme }) => theme.gray[100]} 100%
	);
	border-radius: 16px;
	padding: 56px 64px;
	margin-top: 48px;
	position: relative;
	overflow: hidden;
	border: 1px solid ${({ theme }) => theme.gray[200]};

	// Grid pattern overlay
	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-image: linear-gradient(${({ theme }) => theme.gray[400]}33 1px, transparent 1px),
			linear-gradient(90deg, ${({ theme }) => theme.gray[400]}33 1px, transparent 1px);
		background-size: 40px 40px;
		pointer-events: none;
		opacity: 0.5;
	}
`;

export const HeroContent = styled.div`
	position: relative;
	z-index: 1;
	max-width: 700px;
`;

export const Title = styled.h1`
	font-size: 42px;
	font-weight: 700;
	margin: 0 0 16px 0;
	color: ${({ theme }) => theme.gray[1000]};
	font-family: 'Inter', sans-serif;
`;

export const Description = styled.p`
	font-size: 16px;
	color: ${({ theme }) => theme.gray[700]};
	line-height: 1.6;
	margin: 0 0 16px 0;
	font-family: 'Inter', sans-serif;
`;

export const SearchWrapper = styled.div`
	max-width: 520px;

	// Override Search component styling for hero context
	> div {
		width: 100%;
		margin-right: 0;

		> div:first-child {
			width: 100%;
			height: 52px;
			background: ${({ theme }) => theme.gray[100]};
			border: 1px solid ${({ theme }) => theme.gray[400]};
			border-radius: 12px;

			input {
				font-size: 15px;

				// Remove blue focus ring and use gray border instead
				&:focus {
					outline: none;
					border-color: ${({ theme }) => theme.gray[200]};
				}
			}
		}
	}
`;

//----------------------------------------------------------------------
// FEATURED CARD (for single-page sections like Foundations)
//----------------------------------------------------------------------
export const FeaturedCard = styled.button`
	background: ${({ theme }) => theme.gray[100]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 16px;
	padding: 32px 40px;
	position: relative;
	text-align: left;
	min-height: 120px;
	transition: all 0.15s ease;
	font-family: 'Inter', sans-serif;
	display: flex;
	flex-direction: column;

	&:hover {
		background: ${({ theme }) => theme.gray[200]};
		border-color: ${({ theme }) => theme.gray[400]};
	}

	&:active {
		background: ${({ theme }) => theme.gray[200]};
	}

	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.blue[500]};
		outline-offset: 2px;
	}
`;

export const FeaturedTitle = styled.h2`
	font-size: 22px;
	font-weight: 700;
	color: ${({ theme }) => theme.gray[1000]};
	margin: 0 0 8px 0;
	display: flex;
	align-items: center;
	gap: 12px;
`;

export const FeaturedDescription = styled.p`
	font-size: 14px;
	color: ${({ theme }) => theme.gray[700]};
	line-height: 1.6;
	margin: 0 0 16px 0;
	flex: 1;
`;

export const CtaBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: ${({ theme }) => theme.gray[600]};
	font-size: 13px;
	font-weight: 600;
	transition: all 0.15s ease;
	align-self: flex-end;

	${FeaturedCard}:hover & {
		color: ${({ theme }) => theme.gray[700]};
	}
`;

//----------------------------------------------------------------------
// SECTION CARD (for multi-item sections like Guidelines, Components)
//----------------------------------------------------------------------
export const SectionCard = styled.div`
	background: ${({ theme }) => theme.gray[100]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 16px;
	padding: 40px;
`;

export const SectionTitle = styled.h2`
	font-size: 22px;
	font-weight: 700;
	color: ${({ theme }) => theme.gray[1000]};
	margin: 0 0 12px 0;
	font-family: 'Inter', sans-serif;
	display: flex;
	align-items: center;
	gap: 12px;
`;

export const SectionIntro = styled.p`
	font-size: 14px;
	color: ${({ theme }) => theme.gray[700]};
	line-height: 1.7;
	margin: 0 0 24px 0;
	max-width: 700px;
	font-family: 'Inter', sans-serif;
`;

//----------------------------------------------------------------------
// LIST SECTION
//----------------------------------------------------------------------
export const ItemsList = styled.div`
	display: flex;
	flex-direction: column;
`;

export const ItemLink = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 12px;
	border: none;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
	background: none;
	text-align: left;
	font-size: 15px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[1000]};
	font-family: 'Inter', sans-serif;
	transition: all 0.15s ease;
	border-radius: 6px;

	&:last-child {
		border-bottom: none;
	}

	&:hover {
		background: ${({ theme }) => theme.gray[200]};
	}

	&:active {
		background: ${({ theme }) => theme.gray[400]};
	}

	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.blue[500]};
		outline-offset: 2px;
	}
`;

export const ArrowIcon = styled.span`
	opacity: 0;
	transition: all 0.15s ease;
	display: flex;
	align-items: center;
	color: ${({ theme }) => theme.gray[600]};

	${ItemLink}:hover & {
		opacity: 1;
		color: ${({ theme }) => theme.gray[900]};
	}
`;

//----------------------------------------------------------------------
// ITEMS GRID
//----------------------------------------------------------------------
export const AllItemsLink = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 12px;
	margin-bottom: 20px;
	border: none;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
	background: none;
	text-align: left;
	font-size: 15px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[1000]};
	font-family: 'Inter', sans-serif;
	transition: all 0.15s ease;
	border-radius: 6px;
	width: 100%;

	&:hover {
		background: ${({ theme }) => theme.gray[200]};
	}

	&:active {
		background: ${({ theme }) => theme.gray[400]};
	}

	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.blue[500]};
		outline-offset: 2px;
	}
`;

export const AllLinkArrow = styled.span`
	opacity: 0;
	transition: all 0.15s ease;
	display: flex;
	align-items: center;
	color: ${({ theme }) => theme.gray[600]};

	${AllItemsLink}:hover & {
		opacity: 1;
		color: ${({ theme }) => theme.gray[900]};
	}
`;

export const ItemsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 12px;

	@media (max-width: 1200px) {
		grid-template-columns: repeat(3, 1fr);
	}

	@media (max-width: 900px) {
		grid-template-columns: repeat(2, 1fr);
	}
`;

export const ItemTile = styled.button`
	background: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 10px;
	height: 72px;
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 16px;
	font-size: 13px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[1000]};
	font-family: 'Inter', sans-serif;
	transition: all 0.15s ease;

	&:hover {
		border-color: ${({ theme }) => theme.gray[400]};
		background: ${({ theme }) => theme.gray[100]};
	}

	&:active {
		background: ${({ theme }) => theme.gray[200]};
	}

	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.blue[500]};
		outline-offset: 2px;
	}
`;
